import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/styles";
import { Grid, Theme } from "@mui/material";
import _, { isEqual } from "lodash";
import { v1 } from "uuid";
import {
  IFlightTemplate,
  IOperator,
  IFlight,
  IGroundEvent,
  IPairingPositionCounts,
  IFlightType,
  INewTemplateFlight,
  INewFlight,
  getDefaultPlannedCreditMap,
  IPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";
import { EventTypes } from "./FlightBuilder";
// Bulk Pairing Imports
import { IBuildPayload, IUpdateTableEvents } from "./BuildStatus/BuildStatus";
import { BulkPairingInfo } from "./BulkPairingInfo";
// Shared field imports.
import classNames from "classnames";
import { Card, CardContent, Typography, alpha } from "@mui/material";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { RRuleField } from "src/components/FormikComponents/RRuleField";
import { PolicyResource } from "@vmosolutions/oc-interfaces";
import { FlightRows } from "./Forms/FlightRows";
import { newFlightTemplate, getInitialValues, validateFlight } from "./Forms/FlightRowEdit";

export interface ISharedfieldChangedValues {
  rRule: string;
  operator: IOperator;
  pairingPositionCounts: IPairingPositionCounts;
  includeSplits: boolean;
  isLocal: boolean;
  plannedCredit: number;
}

interface IRecurringFlightsProps {
  id: string;
  path: string[];
  eventType: { value: EventTypes; label: string };
  openFileTreeDrawer?: (event: any) => void;
  fileTreeOpen?: boolean;
  setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  sendBuildStatus?: (buildPayload: IBuildPayload) => void;
  templateChangedState?: (templateChanges: IFlightTemplate) => void;
  updateTableEvents: IUpdateTableEvents;
  defaultFlightType?: IFlightType;
  startDate?: number;
  buildAsLocal: boolean;
  setBuildAsLocal: (state: boolean) => void;
  setDialogData: (state: any) => any;
}

const FlightBuilderFormStyles = (theme: Theme): any => ({
  height: "fit-content",
  padding: "10px 7px",
  marginTop: "10px",
  minWidth: 1120,
  "& .formContainer": {
    "& .pairing_creator_info_card, &.flights_form_card, &.recurring_flights_form_card, &.ground_events_form_card": {
      flexGrow: 1,
      boxShadow: theme.shadows[5],
      margin: "0px 8px 0 8px", //"0px 8px 8px 8px",
      marginTop: "5px",
      backgroundColor: theme.custom.timeline.contextListBackground,
    },
    "&.time_mode_selector": {
      justifyContent: "flex-start",
    },
    padding: "10px",
  },
  "& .tableContainer": {
    borderRadius: "5px",
    marginLeft: "8px !important",
    marginTop: "6px !important",
    flexGrow: 1,
    boxShadow: theme.shadows[5],
    backgroundColor: theme.custom.timeline.contextListBackground,
  },
  "& .gridContainer": {
    width: "100%",
    height: "100%",
    display: "flex",
    alignContent: "flex-start",
    position: "relative",
    padding: "10px",
    flexGrow: 1,
    "& .MuiGrid-item": { width: 363 },
    "& .dialog_spinner_progress": { margin: "-10px" },
    "& .field-group": {
      flexDirection: "row",
      width: 380,
    },
    "& .formik_field": {
      width: 363,
      "&.note, &.comment": { width: 736 },
    },
    "& p.required_fields_label, & .crewslot_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields, & .crewslot_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
    },
    "& .flight_number": {},
    "& .day_offset, .block_duration": {
      width: "auto",
      "& .input_control": {
        "& label": { width: 80 },
        "& div.input-base": {
          maxWidth: 100,
        },
      },
    },
    "& .oc_datetime": {
      // used in flight
      alignItems: "flex-start",
    },
    "& .scheduled_out, & .scheduled_in": {
      // used in recurring flight
      width: "auto",
      alignItems: "flex-start",
      "& .input_control": {
        "& label": { width: 80 },
        "& .time-input": { width: 70 },
      },
    },
    "& .position_count": {
      width: "auto",
      "& label": { width: "auto" },
      "& .input_control": {
        "& div.input-base": {
          maxWidth: 50,
        },
      },
    },
  },
});

// Check template for uniqueID's on legs, if not present add (used for table display and selection)
const addUniqueIDsAndUpdateFlightTypeAndUpdateFlightType = (
  activeFileTemplate: IFlightTemplate,
  flightTypeOptions: IFlightType[]
): IFlightTemplate => {
  activeFileTemplate.legs.map((flight: any) => {
    if (!flight._id) {
      flight._id = `temp-${v1()}`;
    }
    if (flight?.flightType) {
      flight.flightType = flightTypeOptions.find((option: IFlightType) => option._id === flight.flightType?._id);
    }
    return false;
  });
  return activeFileTemplate;
};

// Strip out any invalid flights before sending to build status tool.
const showOnlyValidFlights = (flights: INewTemplateFlight[]): INewTemplateFlight[] => {
  return flights?.filter((f) => !!f?._id.includes("temp") && Object.entries(validateFlight(f))?.length === 0);
};

/////////////////////
// RECURRING FLIGHTS
/////////////////////
export const RecurringFlights = (props: IRecurringFlightsProps): JSX.Element => {
  const theme: Theme = useTheme();

  const {
    id,
    eventType,
    sendBuildStatus,
    templateChangedState,
    updateTableEvents,
    defaultFlightType,
    startDate,
    buildAsLocal,
    setBuildAsLocal,
    setDialogData,
  } = props;
  const activeFileTemplate: IFlightTemplate = useSelector(
    (state: IRootState) => state.flightBuilderTemplates.activeFileTemplate
  );

  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const creditSetting = useMemo(() => {
    return creditSettingValue.settings ?? [];
  }, [creditSettingValue]);

  const initialPlannedCreditMap = useMemo<IPlannedCreditMap>(() => {
    if (creditSetting && creditSetting.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSetting);
      return defaultMap;
    }
    return undefined;
  }, [creditSetting]);

  const defaultAsLocal: boolean = useSelector(
    (state: IRootState) =>
      state.structures.customerStructure.globalAppConfig?.customer?.settings?.recurringFlightsDefaultAsLocal ?? true
  );

  // Create initial form values.
  const flightTemplate: IFlightTemplate = useMemo(
    (): IFlightTemplate => ({
      _id: null,
      ...{
        ...newFlightTemplate,
        asLocal: defaultAsLocal,
        plannedCreditMap: getDefaultPlannedCreditMap(creditSetting),
        legs: [getInitialValues(defaultFlightType)],
      },
    }),
    [defaultFlightType, creditSetting, defaultAsLocal]
  );
  const operatorsOptions: IOperator[] = useSelector(
    (state: IRootState) => state.structures.customerStructure.operators
  );

  // If there is only one operator entry present, set it as default and hide field.
  const defaultOperator = operatorsOptions?.length === 1 ? operatorsOptions[0] : undefined;

  const flightTypes: IFlightType[] = useSelector((state: IRootState) => state.structures.customerStructure.flightTypes);
  const flightTypeOptions = useMemo(() => {
    return flightTypes?.length ? flightTypes.filter((type: IFlightType) => type.isFlight) : [];
  }, [flightTypes]);

  // Template Flights
  const [loadedTemplate, setLoadedTemplate] = useState<IFlightTemplate>(flightTemplate);
  const [templateFile, setTemplateFile] = useState<IFlightTemplate>(flightTemplate);
  const templateLegsAreDirty = !isEqual(loadedTemplate?.legs, templateFile?.legs);

  const validTemplateFlights = useMemo(() => showOnlyValidFlights(templateFile?.legs), [templateFile?.legs]);

  // Additional Fields (do not include these in the flight form)
  const mosaicPane = useMemo(() => document.getElementById(`${id}-panel`), [id]);
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);

  // Compared original loaded template to it's current value, if modified, enable save button
  useEffect(() => {
    if (loadedTemplate && templateFile && !isEqual(loadedTemplate, templateFile)) {
      templateChangedState(templateFile);
    }
  }, [loadedTemplate, templateFile, templateChangedState]);

  // As the status sends back info on successfully updated recurring flights...
  // Either remove them from pending table or change their temp ids to actuals.
  useEffect(() => {
    if (
      !updateTableEvents ||
      (updateTableEvents.hasOwnProperty("cleanOut") && Object.keys(updateTableEvents)?.length === 1)
    ) {
      return;
    }

    setTemplateFile((current) => {
      const updatedLegs = !updateTableEvents?.cleanOut
        ? // if recurring flight successfully built, leave in table but replace it's temp ids.
          current.legs.map((event) => {
            if (updateTableEvents[event._id]) {
              return { ...event, _id: updateTableEvents[event._id] };
            }
            return event;
          })
        : // REMOVE any recurring flights that have successfully been build.
          current.legs.filter((event) => !updateTableEvents[event._id]);
      return { ...current, legs: updatedLegs };
    });
  }, [updateTableEvents]);

  // Initial Load - If activeFileTemplate is loaded and doesn't match loadedTemplate, load it.
  // loadedTemplate is meant to be a copy of the activeFileTemplate (at load) and used only for reference.
  useEffect(() => {
    if (!!activeFileTemplate && !_.isEqual(activeFileTemplate, loadedTemplate) && flightTypeOptions) {
      const templateInfo =
        !!activeFileTemplate.legs && activeFileTemplate.legs.length > 0
          ? addUniqueIDsAndUpdateFlightTypeAndUpdateFlightType(activeFileTemplate, flightTypeOptions)
          : activeFileTemplate;
      // Used deep clones here to avoid potential redux mutations (spread operators weren't working).
      setLoadedTemplate(_.cloneDeep(templateInfo));
      setTemplateFile(_.cloneDeep(templateInfo));
      // const { rRule, operator, pairingPositionCounts, includeSplits, asLocal } = templateInfo;
      // setInitialFormValues((prev) => ({ ...prev, operator, rRule, pairingPositionCounts, includeSplits, asLocal }));
    } else if (!activeFileTemplate && !!loadedTemplate) {
      // Reset if no active template found
      setLoadedTemplate(flightTemplate);
      setTemplateFile(flightTemplate);
    }
  }, [activeFileTemplate, loadedTemplate, flightTemplate, flightTypeOptions, defaultOperator]);

  // The build reads templateFile.asLocal, so the shown time mode must follow it.
  useEffect(() => {
    setBuildAsLocal(!!templateFile?.asLocal);
  }, [templateFile?.asLocal, setBuildAsLocal]);

  // Update Build Status payload and sent to Build Status component.
  // Create deep clone of the recurring flights array (avoids mutation by the status tool)
  useEffect(() => {
    const recurringEvents = _.cloneDeep(templateFile);
    sendBuildStatus({ templateFile: { ...recurringEvents, legs: validTemplateFlights } });
  }, [templateFile, validTemplateFlights, sendBuildStatus]);

  //TODO Add a modal to this call so that users can't accidentally remove a flight.
  /**
   * Remove a selected flight leg
   */
  const removeFlightLeg = useCallback(
    (id: string): void => {
      if (!templateFile.legs) return;
      const updatedFlights = [...templateFile.legs];
      const findIndex = updatedFlights?.findIndex((f) => f?._id == id);
      if (findIndex > -1) {
        updatedFlights.splice(findIndex, 1);
        //updatedFlights.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setTemplateFile((prev) => ({ ...prev, legs: updatedFlights }));
      }
    },
    [templateFile]
  );

  /**
   * Add a New flight leg
   */
  const addFlightLeg = useCallback((): void => {
    const lastFlight = templateFile?.legs[templateFile?.legs?.length - 1];
    const newLeg = {
      ...getInitialValues(defaultFlightType),
      ...{
        flightNumber: lastFlight?.flightNumber, // prefill
        origin: lastFlight?.destination, // prefill original with last flights destination
        flightType: lastFlight?.flightType, // prefill
        scheduledOut: lastFlight?.scheduledIn ?? "00:00", // prefill scheduled out with last flights scheduled in.
        aircraft: lastFlight?.aircraft, // prefill
        aircraftConfiguration: lastFlight?.aircraftConfiguration, // prefill
      },
    };
    setTemplateFile((prev) => ({ ...prev, legs: [...templateFile.legs, newLeg] }));
  }, [defaultFlightType, templateFile]);

  /**
   * Function fired off if user updated a selected flight leg.
   */
  const updateFlightLeg = useCallback(
    (formValues: INewFlight | INewTemplateFlight): void => {
      if (!templateFile.legs) return;
      const updatedFlights = [...templateFile.legs];
      const findIndex = updatedFlights?.findIndex((f) => f?._id == formValues?._id);
      if (findIndex > -1) {
        updatedFlights[findIndex] = formValues as INewTemplateFlight;
        //updatedFlights?.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setTemplateFile((prev) => ({ ...prev, legs: updatedFlights }));
      }
    },
    [templateFile]
  );

  // Reset all changes to template legs
  const resetAllChangesToLegs = useCallback(() => {
    setTemplateFile((prev) => ({ ...prev, legs: loadedTemplate?.legs }));
  }, [loadedTemplate?.legs]);

  return (
    <Grid container sx={FlightBuilderFormStyles(theme)} direction="column" wrap="nowrap">
      <Grid item className={"formContainer"} sx={{ minWidth: "1200px" }}>
        <Card classes={{ root: classNames("recurring_flights_form_card") }}>
          <CardContent className="card_content" style={{ padding: 10 }}>
            <Grid container direction="column">
              <Grid container className={"form_body gridContainer"} style={{ padding: 0 }}>
                <Typography className="required_fields_label">required fields</Typography>
                <Grid container direction="row" className="required_fields">
                  {operatorsOptions?.length > 1 && (
                    <SelectField
                      label="Operator"
                      name="operator"
                      value={templateFile.operator}
                      onChange={(operator) => {
                        setTemplateFile((prev) => ({ ...prev, operator }));
                      }}
                      options={operatorsOptions}
                      getOptionLabel={(option: IOperator) => `${option.title} (${option.description})`}
                      getOptionValue={(option: any) => option._id}
                      isSearchable={true}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      openMenuOnFocus={false}
                      isClearable={false}
                      disabled={!hasPermissions(PolicyResource.Flight, "operator")}
                      meta={{
                        value: templateFile.operator,
                        touched: templateFile?.operator?._id !== loadedTemplate?.operator?._id,
                        initialValue: loadedTemplate.operator,
                      }}
                    />
                  )}

                  <Grid container sx={{ flexGrow: 1, width: "auto" }}>
                    <RRuleField
                      name="rRule"
                      value={templateFile.rRule}
                      onChange={(rRule) => {
                        setTemplateFile((prev) => ({ ...prev, rRule }));
                      }}
                      saveButtonLabel={"Update Recurrence"}
                      onReset={() => {
                        setTemplateFile((prev) => ({ ...prev, rRule: loadedTemplate.rRule }));
                      }}
                      label="Recurrence"
                      selectionFieldProps={{
                        menuPortalTarget: mosaicPane,
                        menuPosition: "fixed",
                      }}
                      meta={{
                        value: templateFile.rRule,
                        touched: templateFile?.rRule !== loadedTemplate?.rRule,
                        initialValue: loadedTemplate.rRule,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item className={"formContainer"} sx={{ minWidth: "1250px" }}>
        <FlightRows
          mosaicId={id}
          flightLegs={templateFile?.legs ?? []}
          legsAreDirty={templateLegsAreDirty}
          resetAllChangesToLegs={resetAllChangesToLegs}
          onSubmit={updateFlightLeg}
          addFlightLeg={addFlightLeg}
          removeFlightLeg={removeFlightLeg}
          eventType={eventType.value}
          startDate={startDate}
          buildAsLocal={buildAsLocal}
          setBuildAsLocal={(state) => {
            setTemplateFile((prev) => ({ ...prev, asLocal: state }));
          }}
          setDialogData={setDialogData}
        />
      </Grid>

      <Accordion className={"tableContainer"} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {`Pending Recurrence Pattern (${validTemplateFlights?.length})`}
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column" style={{ width: "100%" }}>
            <BulkPairingInfo
              initialPairingPosCounts={loadedTemplate.pairingPositionCounts}
              pairingPosCounts={templateFile.pairingPositionCounts}
              setPairingPosCounts={(pairingPositionCounts) => {
                setTemplateFile((prev) => ({ ...prev, pairingPositionCounts }));
              }}
              recurringFlight={true}
              initialIncludeSplits={loadedTemplate.includeSplits}
              includeSplits={templateFile.includeSplits}
              setIncludeSplits={(includeSplits) => {
                setTemplateFile((prev) => ({ ...prev, includeSplits }));
              }}
              initialIsBiddable={loadedTemplate.isBiddable}
              isBiddable={templateFile.isBiddable}
              setIsBiddable={(isBiddable) => {
                setTemplateFile((prev) => ({ ...prev, isBiddable }));
              }}
              initialPlannedCredit={loadedTemplate.plannedCredit ?? 0}
              plannedCredit={templateFile.plannedCredit ?? 0}
              setPlannedCredit={(plannedCredit) => {
                setTemplateFile((prev) => ({ ...prev, plannedCredit }));
              }}
              hidePlannedCredit={creditSettingValue.hidePlannedCreditField}
              initialPlannedCreditMap={loadedTemplate.plannedCreditMap ?? { ...initialPlannedCreditMap }}
              plannedCreditMap={templateFile.plannedCreditMap ?? getDefaultPlannedCreditMap(creditSetting)}
              setPlannedCreditMap={(plannedCreditMap) => {
                setTemplateFile((prev) => ({ ...prev, plannedCreditMap }));
              }}
            />
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
